import * as React from 'react'

import { useFilters } from './useFilters'
import { useJobSearchQuery } from './useJobSearchQuery'
import { useJobSearchParams } from './useJobSearchParams'
import { useJobSearchForm } from './useJobSearchForm'
import useTypingAnimator from './useTypingAnimator'
import { useWalkthrough } from './WalkthroughProvider'
import { useTranslation } from '~/hooks/useTranslation'

const JobSearchContext = React.createContext()

export function useJobSearch() {
  return React.useContext(JobSearchContext)
}

export function JobSearchProvider({
  search_results,
  error,
  total_results,
  total_pages,
  next_page,
  saved_params,
  allow_debug,
  isIntooItaly,
  isDemo,
  keySkills,
  showTalentguide,
  availableResumes,
  children,
  runWalkthrough,
  walkthroughCompany,
  walkthroughLocation,
  testing,
  radii,
  north_american,
}) {
  const [selectedJobDrawerOpen, setSelectedJobDrawerOpen] =
    React.useState(false)
  const resultDetailScrollRef = React.useRef(null)
  const [selectedJob, setSelectedJob] = React.useState(null)
  const [noResultQuery, setNoResultQuery] = React.useState('')
  const [viewMoreButtonDisabled, setViewMoreButtonDisabled] =
    React.useState(false)
  const [formDisabled, setFormDisabled] = React.useState(false)
  const {
    state: { started, stepIndex },
  } = useWalkthrough()

  const { t } = useTranslation('')
  const sample_title = t('Walkthrough.keyword.sample_title')
  const whatVal = useTypingAnimator(started, sample_title)
  const companyVal = useTypingAnimator(stepIndex === 1, walkthroughCompany)
  const whereVal = useTypingAnimator(stepIndex === 2, walkthroughLocation)
  const { urlSearchParams, setUrlSearchParams, paramString, lockParamString } =
    useJobSearchParams()
  const [savedJobIds, setSavedJobIds] = React.useState([])
  const debug = allow_debug && urlSearchParams.has('debug')
  const [previousFirstJobId, setPreviousFirstJobId] = React.useState(null)
  const [showFeedbackDialog, setShowFeedbackDialog] = React.useState(false)
  const toggleShowFeedbackDialog = () => setShowFeedbackDialog((prev) => !prev)
  const quickSearchButtonRef = React.useRef(null)
  const [fakeDisabled, setFakeDisabled] = React.useState(false)

  const fakeQuickSearch = () => {
    setFakeDisabled(true)
    setTimeout(() => {
      setFakeDisabled(false)
    }, 500)
  }

  const {
    queryStatusMessage,
    pagesToShow,
    setPagesToShow,
    jobResults,
    companyList,
    amountShown,
    amountTotal,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isPending,
    showViewMoreButton,
  } = useJobSearchQuery({
    paramString,
    runWalkthrough,
    initialData: {
      pageParams: [1],
      pages: [
        {
          results: search_results,
          total_results: total_results,
          total_pages: total_pages,
          next_page: next_page,
          error: error,
        },
      ],
    },
  })

  const { form, onSubmit } = useJobSearchForm({
    urlSearchParams,
    lockParamString,
    noResultQuery,
    setPagesToShow,
    saved_params,
    whatVal,
    companyVal,
    whereVal,
    isIntooItaly,
    isDemo,
  })

  const { hasFilters, resetFilters, sort, setSort } = useFilters({
    form,
    setUrlSearchParams,
    walkthroughStarted: started,
    urlSearchParams,
  })

  // Disable form when fetching
  // but only when we have only one page to show
  // and we are not fetching the next page in the background
  React.useEffect(() => {
    if (isFetching && pagesToShow === 1 && !isFetchingNextPage) {
      setFormDisabled(true)
    } else {
      setFormDisabled(false)
    }
  }, [pagesToShow, isFetching, isFetchingNextPage, isPending])

  // Set selected job when job results are updated
  // but only if the first job has changed
  const firstJob = jobResults[0]
  React.useEffect(() => {
    if (firstJob && firstJob.id !== previousFirstJobId) {
      setPreviousFirstJobId(firstJob.id)
      setSelectedJob(firstJob)
    }
  }, [firstJob, previousFirstJobId])

  const urlSearchParamsWhat = urlSearchParams.get('what')
  const urlSearchParamsWhere = urlSearchParams.get('where')
  React.useEffect(() => {
    if (jobResults.length === 0) {
      setNoResultQuery(urlSearchParamsWhat || urlSearchParamsWhere)
    } else {
      setNoResultQuery('')
    }
  }, [
    jobResults.length,
    urlSearchParamsWhat,
    paramString,
    urlSearchParamsWhere,
  ])

  const handleSelectJob = (job) => (event) => {
    event.stopPropagation()
    resultDetailScrollRef.current?.scrollTo(0, 0, { behavior: 'smooth' })
    setSelectedJob(job)
    setSelectedJobDrawerOpen(true)
  }

  const handleViewMore = async () => {
    setViewMoreButtonDisabled(true)
    setPagesToShow((prev) => prev + 1)
    await fetchNextPage()
    setViewMoreButtonDisabled(false)
  }

  const value = {
    form,
    formDisabled,
    onSubmit,
    setSelectedJob,
    selectedJob,
    hasFilters,
    resetFilters,
    selectedJobDrawerOpen,
    setSelectedJobDrawerOpen,
    resultDetailScrollRef,
    noResultQuery,
    handleSelectJob,
    handleViewMore,
    urlSearchParams,
    lockParamString,
    setPagesToShow,
    queryStatusMessage,
    setNoResultQuery,
    jobResults,
    error,
    amountShown,
    amountTotal,
    viewMoreButtonDisabled,
    showViewMoreButton,
    savedJobIds,
    setSavedJobIds,
    debug,
    isIntooItaly,
    isDemo,
    keySkills,
    showTalentguide,
    availableResumes,
    sort,
    setSort,
    runWalkthrough,
    testing,
    whatVal,
    companyVal,
    whereVal,
    showFeedbackDialog,
    toggleShowFeedbackDialog,
    radii,
    companyList,
    quickSearchButtonRef,
    fakeQuickSearch,
    fakeDisabled,
    walkthroughCompany,
    walkthroughLocation,
    north_american,
  }

  return (
    <JobSearchContext.Provider value={value}>
      {children}
    </JobSearchContext.Provider>
  )
}
